/**
 * Page Sections client — fetches the admin-controlled visibility +
 * order for a public page's sections (home, about, …). Falls back to
 * the page's static registry order when the API is unavailable so the
 * page never breaks.
 */

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

export interface PageSectionConfig {
  id: string
  labelAr: string
  labelEn: string
  isVisible: number | boolean
  sortOrder: number
}

interface Envelope<T> {
  success: boolean
  data?: T
  error?: { code: string; message: string }
}

/**
 * Returns the visible sections of `page` in display order. On any
 * failure returns `null` so the caller can fall back to its full static
 * registry.
 */
export async function fetchPageSections(
  page: string,
  signal?: AbortSignal,
): Promise<PageSectionConfig[] | null> {
  if (!API_URL) return null
  try {
    const res = await fetch(`${API_URL}/page-sections/${encodeURIComponent(page)}`, {
      headers: { Accept: 'application/json' },
      signal,
    })
    if (!res.ok) return null
    const json = (await res.json()) as
      | Envelope<PageSectionConfig[]>
      | PageSectionConfig[]
    const list = Array.isArray(json) ? json : json.success ? json.data : null
    return list ?? null
  } catch {
    return null
  }
}

/**
 * Resolve the ordered list of visible section keys for a page.
 *
 * @param config  API result (`undefined` = loading, `null` = failed)
 * @param fallbackKeys  the page's full static registry order
 * @param knownKeys  set of keys the registry knows how to render
 */
export function resolveSectionOrder<K extends string>(
  config: PageSectionConfig[] | null | undefined,
  fallbackKeys: K[],
  knownKeys: Set<string>,
): K[] {
  if (config && config.length > 0) {
    return config
      .filter((s) => Boolean(s.isVisible) && knownKeys.has(s.id))
      .map((s) => s.id as K)
  }
  return fallbackKeys
}
